import React from 'react';

const sectionStyle = {
    marginBottom: '2.5rem',
};

const h2Style = {
    fontSize: 'clamp(1.25rem, 2vw, 1.75rem)',
    marginBottom: '0.85rem',
    lineHeight: 1.15,
};

const pStyle = {
    fontSize: '1rem',
    lineHeight: 1.8,
    marginBottom: '1rem',
    maxWidth: '72ch',
};

export default function PrivacyPolicyPage() {
    return (
        <main
            style={{
                minHeight: '100vh',
                color: 'var(--color-text, #2a2a2a)',
            }}
        >
            <section
                style={{
                    borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.08))',
                    padding: '7rem 1.25rem 2.5rem',
                }}
            >
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <p
                        style={{
                            fontSize: '0.85rem',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            opacity: 0.65,
                            marginBottom: '1rem',
                        }}
                    >
                        Legal / Privacy
                    </p>
                    <h1
                        style={{
                            fontSize: 'clamp(2.5rem, 8vw, 6rem)',
                            lineHeight: 0.95,
                            marginBottom: '1.25rem',
                            maxWidth: '10ch',
                        }}
                    >
                        Privacy Policy
                    </h1>
                    <p style={{ ...pStyle, fontSize: '1.1rem', maxWidth: '60ch' }}>
                        Meshae Studios respects your privacy. This page explains what we collect, how we use it, and what happens if you connect Meta, Instagram, or Threads features through our website or tools.
                    </p>
                    <p style={{ ...pStyle, marginBottom: 0 }}>Last updated: April 17, 2026</p>
                </div>
            </section>

            <section style={{ padding: '3rem 1.25rem 6rem' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gap: '3rem', gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <div style={{ maxWidth: '760px' }}>
                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Who we are</h2>
                            <p style={pStyle}>
                                Meshae Studios is a creative and digital services business. In this policy, “Meshae Studios,” “we,” “our,” and “us” refer to the Meshae Studios website, services, and tools.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>What we collect</h2>
                            <p style={pStyle}>
                                We may collect information you give us directly, including your name, email address, project details, messages, business information, and anything else you choose to submit through forms, email, or account connections.
                            </p>
                            <p style={pStyle}>
                                We may also collect technical information such as IP address, browser type, device information, pages visited, referrers, and usage or log data used to operate, secure, and improve the site.
                            </p>
                            <p style={pStyle}>
                                If you connect a Meta, Instagram, or Threads account, we may receive account information and permissions you authorize, such as account ID, username, profile information, access tokens, and publishing or analytics permissions needed to provide the feature you requested.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>How we use it</h2>
                            <p style={pStyle}>
                                We use information to run the website, respond to inquiries, provide services, maintain connected features, debug issues, improve performance, prevent abuse, and communicate with you about the services or tools you asked for.
                            </p>
                            <p style={pStyle}>
                                If you authorize a Meta, Instagram, or Threads connection, we use that data only as needed to support the connected feature, such as authentication, account linking, publishing support, analytics, or related platform functionality.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>How we share it</h2>
                            <p style={pStyle}>
                                We do not sell your personal information. We may share information with vendors and service providers who help us host, secure, analyze, or operate the website and related services.
                            </p>
                            <p style={pStyle}>
                                We may also share information if required by law, to protect rights and safety, to investigate misuse, or in connection with a business transfer.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Meta, Instagram, and Threads data</h2>
                            <p style={pStyle}>
                                If you use a Meshae Studios feature connected to Meta platforms, we access and use platform data only to deliver the feature you initiated and only in line with this Privacy Policy.
                            </p>
                            <p style={pStyle}>
                                We do not use Meta, Instagram, or Threads data for unrelated purposes, and we do not keep it longer than necessary for the authorized feature, security, legal compliance, or reasonable business records.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Cookies and analytics</h2>
                            <p style={pStyle}>
                                We may use cookies, pixels, logs, and similar technologies to keep the site working, understand usage, remember preferences, and improve the experience. You can usually manage cookies through your browser settings.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Retention</h2>
                            <p style={pStyle}>
                                We keep information only as long as reasonably necessary to provide services, maintain records, resolve disputes, comply with legal obligations, and protect our systems. Social connection tokens and related credentials may be removed when no longer needed or when access is revoked.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Your rights</h2>
                            <p style={pStyle}>
                                Depending on where you live, you may have rights to request access to, correction of, deletion of, or restriction of your personal information, and in some cases to object to certain processing or request data portability.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 id="data-deletion-requests" style={h2Style}>
                                Data deletion requests
                            </h2>
                            <p style={pStyle}>
                                If you connected a Meta, Instagram, or Threads account and want your data deleted, email us with the subject line <strong>Data Deletion Request</strong> and include the account username or email associated with the request.
                            </p>
                            <p style={pStyle}>
                                You may also remove Meshae Studios app access from your Meta account settings. Once we verify your request, we will delete or de-identify information we are not legally required to keep.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Security</h2>
                            <p style={pStyle}>
                                We use reasonable administrative, technical, and organizational safeguards to protect information. No system is perfectly secure, so we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Children’s privacy</h2>
                            <p style={pStyle}>
                                Our website and services are not directed to children under 13, and we do not knowingly collect personal information from children under 13.
                            </p>
                        </section>

                        <section style={sectionStyle}>
                            <h2 style={h2Style}>Changes</h2>
                            <p style={pStyle}>
                                We may update this Privacy Policy from time to time. When we do, we will update the date at the top of the page.
                            </p>
                        </section>

                        <section style={{ ...sectionStyle, marginBottom: 0 }}>
                            <h2 style={h2Style}>Contact</h2>
                            <p style={pStyle}>
                                For privacy questions or data deletion requests, contact <a href="mailto:hello@meshaestudios.com" style={{ color: 'inherit' }}>admin@meshaestudios.com</a>.
                            </p>
                        </section>
                    </div>
                </div>
            </section>
        </main>
    );
}
